import React from 'react';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';

const Testimonials: React.FC = () => {
  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Small Business Owner",
      avatar: "SJ",
      rating: 5,
              text: "Financial Goal Tracker has completely transformed how I manage my business finances. I've saved 30% more this year compared to last year!",
      color: "primary"
    },
    {
      name: "Michael Chen",
      role: "Software Engineer",
      avatar: "MC",
      rating: 5,
      text: "The app's intuitive interface and powerful analytics helped me achieve my savings goal for a down payment on my first home.",
      color: "success"
    },
    {
      name: "Emily Rodriguez",
      role: "Graduate Student",
      avatar: "ER",
      rating: 5,
      text: "As a student on a tight budget, this app has been a lifesaver. I can track my expenses and still save for my future goals.",
      color: "accent"
    },
    {
      name: "David Thompson",
      role: "Retirement Planner",
      avatar: "DT",
      rating: 5,
      text: "I recommend Goal Tracker to all my clients. It's the perfect tool for anyone serious about achieving their financial goals.",
      color: "info"
    },
    {
      name: "James Wilson",
      role: "Freelance Designer",
      avatar: "JW",
      rating: 5,
      text: "Finally, an app that understands the irregular income of freelancers. The flexible goal setting is exactly what I needed.",
      color: "primary"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="testimonials" className="py-20 bg-grey-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-textBase mb-6">
            Loved by
            <span className="gradient-text"> Thousands of Users</span>
          </h2>
          <p className="text-xl text-grey-600 max-w-3xl mx-auto">
            See what our users are saying about their experience with My Goal Tracker 
            and how it's helping them achieve their financial dreams.
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              className={`bg-white rounded-brand-lg p-6 shadow-brand hover:shadow-brand-lg transition-all duration-300 ${
                index === 4 ? 'md:col-span-2 lg:col-span-1' : ''
              }`}
              variants={itemVariants}
              whileHover={{ y: -5 }}
            >
              {/* Quote Icon */}
              <div className={`w-10 h-10 bg-${testimonial.color}/10 rounded-full flex items-center justify-center mb-4`}>
                <Quote className={`w-5 h-5 text-${testimonial.color}`} />
              </div>

              {/* Rating */}
              <div className="flex items-center mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-accent fill-current" />
                ))}
              </div>

              {/* Testimonial Text */}
              <p className="text-grey-700 leading-relaxed mb-6">
                "{testimonial.text}"
              </p>

              {/* Author */}
              <div className="flex items-center">
                <div className={`w-12 h-12 bg-${testimonial.color}/20 rounded-full flex items-center justify-center mr-4`}>
                  <span className={`font-semibold text-${testimonial.color}`}>
                    {testimonial.avatar}
                  </span>
                </div>
                <div>
                  <div className="font-semibold text-textBase">
                    {testimonial.name}
                  </div>
                  <div className="text-sm text-grey-600">
                    {testimonial.role}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Overall Rating */}
        <motion.div
          className="mt-20 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="bg-gradient-to-r from-primary to-primary-dark rounded-brand-lg p-8 shadow-brand max-w-3xl mx-auto text-white">
            <div className="flex items-center justify-center mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-8 h-8 text-accent fill-current" />
              ))}
            </div>
            <h3 className="text-2xl font-bold mb-2">
              4.8 out of 5 stars
            </h3>
            <p className="text-white/90 mb-6">
              Based on 2,847 reviews from satisfied users worldwide
            </p>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-white">98%</div>
                <div className="text-sm text-white/80">Success Rate</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">10K+</div>
                <div className="text-sm text-white/80">Active Users</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">$2.5M</div>
                <div className="text-sm text-white/80">Goals Achieved</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-lg text-grey-600 mb-4">
            Join thousands of satisfied users today
          </p>
          <motion.button
            className="btn-primary"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Start Your Success Story
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials; 